import { type Dispatch, type SetStateAction } from "react";

import {
  UseComboboxProps as UseDownshiftComboboxProps,
  UseMultipleSelectionProps as UseDownshiftMultipleSelectionProps,
} from "downshift";

import {
  defaultItemToString,
  defaultItemToKey,
} from "../Combobox/ComboboxUtils";
import { SelectTriggerBase } from "./internal/SelectTriggerBase";
import { LabelProps } from "../Label";
import { TextFieldProps } from "../TextField";
import { ChipProps } from "../Chip";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

export type SelectTriggerProps<Item = any> = Omit<TextFieldProps, "onChange"> &
  Pick<LabelProps, "required" | "moreInfo" | "openMoreInfo"> &
  LayoutUtilProps & {
    /**
     * Should be a positive integer.
     */
    maxRows?: number;

    /** Allows Combobox/Select to display custom-colored Chips */
    selectedItemProps?: (item: Item) => Partial<ChipProps>;
  } & {
    onClick?: () => void;
    disableClearSelection?: boolean;
  } & Pick<UseDownshiftComboboxProps<Item>, "itemToString" | "itemToKey"> &
  (
    | {
        multiple?: never;
        onChange?:
          | Dispatch<SetStateAction<Item | null>>
          | ((arg: Item | null) => void);
        selectedItem: UseDownshiftComboboxProps<Item>["selectedItem"];
        selectedItems?: never;
      }
    | {
        multiple: true;
        onChange?:
          | Dispatch<SetStateAction<Item[] | null>>
          | ((arg: Item[] | null) => void);
        selectedItem?: never;
        selectedItems: UseDownshiftMultipleSelectionProps<Item>["selectedItems"];
      }
  );

export const SelectTrigger = function <Item = any>(
  props: SelectTriggerProps<Item>,
) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);

  const {
    className,
    style,
    label,
    size,
    error,
    hint,
    description,
    required,
    moreInfo,
    openMoreInfo,
    prefix,
    suffix,
    maxRows,
    selectedItemProps = () => ({}),
    id,
    placeholder,
    disabled: isDisabled,
    readOnly: isReadOnly,
    onClick,
    multiple,
    onChange,
    disableClearSelection,
    itemToString: itemToStringProp,
    itemToKey: itemToKeyProp,
    selectedItem = null,
    selectedItems = [],
    ...rest
  } = componentProps;

  const itemToString = itemToStringProp ?? defaultItemToString<Item>;
  const itemToKey = itemToKeyProp ?? defaultItemToKey<Item>;

  function removeSelectedItem(item: Item) {
    if (multiple) {
      onChange?.(
        selectedItems.filter(
          (selectedItem: Item) => itemToKey(selectedItem) !== itemToKey(item),
        ),
      );
    } else if (itemToKey(selectedItem) === itemToKey(item)) {
      onChange?.(null);
    }
  }

  function reset() {
    if (multiple) {
      onChange?.([]);
    } else {
      onChange?.(null);
    }
  }

  const noClearButton = isDisabled || isReadOnly || disableClearSelection;

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  return (
    <SelectTriggerBase
      data-anv="select-trigger"
      className={className}
      style={styleCombined}
      label={label}
      size={size}
      error={error}
      hint={hint}
      description={description}
      labelProps={{
        required,
        moreInfo,
        openMoreInfo,
      }}
      prefix={prefix}
      suffix={suffix}
      maxRows={maxRows}
      selectedItemProps={selectedItemProps}
      id={id}
      placeholder={placeholder}
      disabled={isDisabled}
      readOnly={isReadOnly}
      itemToString={itemToString}
      removeSelectedItem={removeSelectedItem}
      disableClearSelection={noClearButton}
      selectedItem={selectedItem}
      selectedItems={selectedItems}
      onClearButtonClick={reset}
      inputWrapperProps={{ onClick }}
      inputProps={rest}
    />
  );
};
